import {
  DeleteOutlineIcon,
  DuplicateOutlineIcon,
  MoreVerticalOutlineIcon,
} from '@carboncopyco/icons';
import { Dropdown, DropdownItem, TextBase } from '@carboncopyco/ui';
import { getMaxZIndex } from '@carboncopyco/utils';
import type { Row } from '@tanstack/react-table';
import { useState } from 'react';

export interface RowActionsCellProps {
  row: Row<Record<string, unknown>>;
  value: Record<string, unknown>[] | unknown[];
  onChangeValue: (value: Record<string, unknown>[] | unknown[]) => void;
  isLastRow?: boolean;
}

export const RowActionsCell = ({
  row,
  value,
  onChangeValue,
  isLastRow,
}: RowActionsCellProps) => {
  const duplicateRow = () => {
    const updatedValue = [...value];
    updatedValue.splice(row.index, 0, value[row.index]);
    onChangeValue?.(updatedValue);
  };

  const deleteRow = () => {
    const updatedValue = [...value];
    updatedValue.splice(row.index, 1);
    onChangeValue?.(updatedValue);
  };

  return (
    <div className="flex flex-col justify-center">
      <RowActionsDropdown
        onDuplicate={duplicateRow}
        onDelete={deleteRow}
        deleteDisabled={isLastRow}
      />
    </div>
  );
};

interface RowActionsDropdownProps {
  onDuplicate: () => void;
  onDelete: () => void;
  deleteDisabled?: boolean;
}

const RowActionsDropdown = ({
  onDuplicate,
  onDelete,
  deleteDisabled,
}: RowActionsDropdownProps) => {
  const [dropdownOpen, setDropdownOpen] = useState<boolean>();

  const dropdownItems = [
    {
      label: 'Duplicate',
      icon: DuplicateOutlineIcon,
      onClick: onDuplicate,
    },
    {
      label: 'Delete',
      icon: DeleteOutlineIcon,
      onClick: onDelete,
      disabled: deleteDisabled,
    },
  ];

  return (
    <Dropdown
      interactive
      align="start"
      open={dropdownOpen}
      onOpenChange={setDropdownOpen}
      zIndex={getMaxZIndex() + 1}
      triggerElement={
        <span className="h-full text-ds-natural-300 cursor-pointer">
          <MoreVerticalOutlineIcon size={20} />
        </span>
      }
    >
      {dropdownItems.map(({ label, icon: Icon, onClick, disabled }) => (
        <DropdownItem
          key={label}
          label={
            <TextBase
              label={label}
              color="text-ds-natural-600"
            />
          }
          disabled={disabled}
          iconLeft={
            <div className="text-ds-natural-600">
              <Icon size={20} />
            </div>
          }
          onClick={onClick}
        />
      ))}
    </Dropdown>
  );
};
